import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Image from '../Image';
import { markUserNuxAsSeen } from '../../services/UserService';
import { Mixpanel } from '../../services/Mixpanel';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { badgeSeenNuxKey } from '../../utils/badgeNotification';
import { badgeArtSrc } from '../Badge';

// Per-type congratulations. Each headline is spelled out rather than built from
// a single `Congrats on becoming a Diffit ${name}!` template, because the names
// don't all slot into it — diffit_lead is named "Diffit Lead", which would read
// "a Diffit Diffit Lead". Falls back to the badge's own `name` for a type added
// on the server before this map catches up — see BADGE_TYPES in
// diffit-api/app/routers/admin/user.py.
const BADGE_HEADLINES = {
  ambassador: 'Congrats on becoming a Diffit Ambassador!',
  certified_educator: 'Congrats on becoming a Diffit Certified Educator!',
  certified_educator_l2: 'Congrats on reaching Certified Educator Level 2!',
  star_teacher: 'Congrats on becoming a Diffit Star Teacher!',
  diffit_lead: 'Congrats on becoming a Diffit Lead!',
  dwd_host: 'Thanks for hosting Donuts with Diffit!',
};

const headlineForBadge = (badge) =>
  BADGE_HEADLINES[badge.type] || `Congrats on your new ${badge.name} badge!`;

/**
 * Congratulates a teacher on a badge an admin just granted them, once.
 *
 * Rendered globally from base.jsx whenever getBadgesToAnnounce() finds an
 * unannounced badge. Dismissing writes the `badge_seen_<type>` flag for every
 * badge shown, so the modal never reappears for those badges.
 *
 * @param {Array<{ type: string, name: string, granted_at: string }>} badges non-empty.
 */
export default function BadgeAwardedModal({ badges, onClose }) {
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { diffitUser, refreshDiffitUser } = useDiffitUserContext();

  const isMultiple = badges.length > 1;
  const headline = isMultiple ? 'Congrats on your new badges!' : headlineForBadge(badges[0]);

  useEffect(() => {
    Mixpanel.track('Badge Awarded Modal: Shown', {
      badge_types: badges.map((badge) => badge.type),
    });
  }, []);

  const handleConfirm = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setError(null);

    Mixpanel.track('Badge Awarded Modal: Confirmed', {
      badge_types: badges.map((badge) => badge.type),
    });

    try {
      await markUserNuxAsSeen(
        diffitUser.id,
        Object.fromEntries(badges.map((badge) => [badgeSeenNuxKey(badge.type), true]))
      );
      // Pick up the flags we just wrote, so the effect in base.jsx doesn't
      // re-open the modal off the stale user it's still holding.
      await refreshDiffitUser();
      onClose();
    } catch (err) {
      console.error('Error acknowledging badge:', err);
      // Leave the modal open — closing it without a saved flag would just show
      // it again on the next load.
      setError('Something went wrong. Please try again.');
      Mixpanel.track('Badge Awarded Modal: Error', { error: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full justify-center p-4 text-center items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
              <Dialog.Panel className='relative transform bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-md rounded-2xl px-8 py-8'>
                <div className='flex flex-col items-center gap-5'>
                  {/* Wraps because a bulk sheet import can announce up to
                      MAX_BADGES_PER_USER (6) at once, and six 80px badges plus
                      gaps overflow this panel's 384px of content width. Without
                      shrink-0 the images compress to fit and lose their aspect
                      ratio instead of moving to a second row. */}
                  <div className='flex flex-row flex-wrap items-center justify-center gap-3'>
                    {badges.map((badge) => (
                      <Image
                        key={badge.type}
                        src={badgeArtSrc(badge.type)}
                        alt={`${badge.name} Badge`}
                        className='h-20 w-20 shrink-0'
                      />
                    ))}
                  </div>

                  <Dialog.Title as='h2' className='text-2xl font-bold text-gray-700 text-center'>
                    {headline}
                  </Dialog.Title>

                  {error && (
                    <div className='w-full p-3 bg-red-50 border border-red-200 rounded-lg'>
                      <p className='text-sm text-red-800 text-center'>{error}</p>
                    </div>
                  )}

                  <button
                    type='button'
                    onClick={handleConfirm}
                    disabled={isSaving}
                    className='w-full bg-emerald-600 text-white font-semibold py-3 px-4 rounded-lg shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 transition-colors disabled:opacity-60'>
                    Continue to Diffit
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
